/**
 * RSVP audience split — is the RSVP link reaching the team's extended
 * family circle, or just team regulars on another page?
 *
 * The whole split is computed ON THIS DEVICE, the same way every Pulse
 * web counter works: client-side dedupe, empty payload, no identifier.
 * A browser that has ever opened a team-context page carries a local
 * flag; an RSVP visit then fires exactly one of two events, at most once
 * per ISO week:
 *
 *   rsvp_weekly_team — this browser also uses the product's team side
 *   rsvp_weekly_only — this browser has only ever been reached by links
 *
 * rsvp-only share = only / (only + team), read server-side as plain row
 * counts. Nothing read from localStorage is ever transmitted.
 *
 * Known biases, accepted and stated: the context flag persists forever,
 * so one historical tap into /teams reclassifies a device permanently
 * (undercounts "only"); a team regular whose first-ever visit this week
 * is an RSVP link counts "only" for that week (overcounts "only" once,
 * then settles). Over a multi-week read the flag's persistence wins.
 */
import { pulse } from "@/utils/pulse";
import { isoWeekLabel } from "@/utils/pulse-identity";

const TEAM_CONTEXT_KEY = "pulse_team_context";
const RSVP_WEEK_KEY = "pulse_rsvp_week";

/**
 * Routes that mean "this browser uses the product's team side".
 * Deliberately NOT the shared-link routes (/rsvp/:id, /game/:id,
 * /rpe/:token, vanity redirects) and not the public info/legal pages —
 * those are exactly the surfaces a family-circle visitor lands on.
 */
const TEAM_CONTEXT_PREFIXES = [
  "/teams",
  "/dashboard",
  "/create",
  "/settings",
  "/coach-log",
  "/stats",
  "/admin",
  "/parent",
  "/login",
];

export function isTeamContextPath(pathname: string): boolean {
  return TEAM_CONTEXT_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
}

export function isRsvpPath(pathname: string): boolean {
  return /^\/rsvp\/[^/]+$/.test(pathname);
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Call on every route change. Sets the team-context flag on team routes;
 * on an RSVP route, fires the weekly audience event — at most one per
 * browser per ISO week, and only if the dedupe marker could actually be
 * written first (an unbounded weekly event is worse than a missing one).
 */
export function trackRsvpAudience(
  pathname: string,
  send: (event: string) => void = pulse,
): void {
  try {
    if (typeof window === "undefined") return;

    if (isTeamContextPath(pathname)) {
      safeSet(TEAM_CONTEXT_KEY, "1");
      return;
    }

    if (!isRsvpPath(pathname)) return;

    const week = isoWeekLabel();
    if (safeGet(RSVP_WEEK_KEY) === week) return;
    if (!safeSet(RSVP_WEEK_KEY, week)) return;
    send(safeGet(TEAM_CONTEXT_KEY) ? "rsvp_weekly_team" : "rsvp_weekly_only");
  } catch {
    /* never throw into the host page */
  }
}
